/* Adaptive navigation: native page scroll on mobile, Horizon's wrapper on desktop. */
const initNuvoriteHeaders = (root = document) => {
  root.querySelectorAll('[data-nv-header-root]:not([data-nv-header-ready])').forEach((host) => {
    const header = host.querySelector('[data-nv-header]');
    const space = host.querySelector('[data-nv-header-space]');
    const announcement = host.querySelector('[data-nv-announcement]');
    const menu = host.querySelector('.nv-mobile-menu');
    const opener = host.querySelector('[data-nv-country-open]');
    const dialog = host.querySelector('[data-nv-country-dialog]');
    const dropdown = dialog?.querySelector('[data-nv-country-dropdown]');
    const form = dialog?.querySelector('form');
    const wrapper = host.closest('.page-wrapper');
    if (!header || !space || !opener || !dialog || !form) return;
    const events = new AbortController();
    const on = (target, name, callback, options = {}) => target?.addEventListener(name, callback, { ...options, signal: events.signal });
    let frame, resizeFrame, scroller = window, threshold = 0, lastY = 0, direction = 0, travel = 0;
    let measuredHeaderHeight = 0, measuredAnnouncementHeight = 0;
    let lockedScrollY = null;
    const scrollY = () => {
      const page = document.documentElement;
      const pageY = Math.max(0, Math.min(window.scrollY, Math.max(0, page.scrollHeight - page.clientHeight)));
      if (scroller === window) return pageY;
      const nestedY = Math.max(0, Math.min(scroller.scrollTop, Math.max(0, scroller.scrollHeight - scroller.clientHeight)));
      // Horizon can retain root-page scroll when a viewport switches to its desktop scroller.
      return pageY + nestedY;
    };
    const reveal = () => header.classList.remove('is-scroll-hidden');
    const engaged = () => Boolean(menu?.open || dialog.open);
    const update = () => {
      frame = null;
      const y = scrollY();
      const delta = y - lastY;
      const fixed = y > threshold;
      header.classList.toggle('is-fixed', fixed);
      if (!fixed || y <= threshold + 8 || engaged()) {
        reveal(); travel = 0; direction = 0;
      } else if (Math.abs(delta) >= 1) {
        const nextDirection = Math.sign(delta);
        travel = nextDirection === direction ? travel + Math.abs(delta) : Math.abs(delta);
        direction = nextDirection;
        if (direction < 0 && travel >= 8) reveal();
        if (direction > 0 && travel >= 12 && y > threshold + header.offsetHeight) header.classList.add('is-scroll-hidden');
      }
      lastY = y;
    };
    const schedule = () => { if (frame == null) frame = requestAnimationFrame(update); };
    const measure = () => {
      resizeFrame = null;
      if (!dialog.open) scroller = wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper : window;
      measuredHeaderHeight = header.offsetHeight;
      measuredAnnouncementHeight = announcement?.offsetHeight || 0;
      space.style.height = `${measuredHeaderHeight}px`;
      const y = scrollY();
      threshold = Math.max(0, space.getBoundingClientRect().top + y);
      lastY = y; direction = 0; travel = 0;
      header.classList.toggle('is-fixed', y > threshold);
      reveal();
    };
    const scheduleMeasure = () => { cancelAnimationFrame(resizeFrame); resizeFrame = requestAnimationFrame(measure); };
    const restoreScrollLock = () => {
      document.documentElement.classList.remove('nv-country-modal-open');
      if (lockedScrollY != null) {
        // Closing the dialog must not turn a stationary page into a false scroll gesture.
        lastY = scrollY(); lockedScrollY = null; direction = 0; travel = 0;
      }
    };
    const closeCountry = () => { if (dialog.open) dialog.close(); };
    on(opener, 'click', () => {
      if (menu) menu.open = false;
      reveal();
      if (dialog.open) return;
      lockedScrollY = scrollY();
      dialog.showModal();
      document.documentElement.classList.add('nv-country-modal-open');
      opener.setAttribute('aria-expanded', 'true');
      dialog.querySelector('[data-nv-country-close]')?.focus({ preventScroll: true });
    });
    on(dialog.querySelector('[data-nv-country-close]'), 'click', closeCountry);
    on(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeCountry();
    });
    on(dialog, 'close', () => {
      if (dropdown) dropdown.open = false;
      restoreScrollLock();
      opener.setAttribute('aria-expanded', 'false');
      reveal(); opener.focus({ preventScroll: true });
    });
    on(dialog, 'cancel', () => { /* Native Escape closes the top-layer dialog and restores focus. */ });
    on(dialog, 'click', (event) => {
      const option = event.target.closest('[data-nv-country-option]');
      if (!option || !dialog.contains(option)) return;
      const code = option.dataset.countryCode;
      const input = form.querySelector('input[name="country_code"]');
      if (!code || !input) return;
      if (code === dialog.dataset.currentCountry) { closeCountry(); return; }
      input.value = code;
      form.requestSubmit();
    });
    // Guard programmatic submits too: only rendered, enabled Shopify markets may be sent.
    on(form, 'submit', (event) => {
      const value = form.querySelector('input[name="country_code"]')?.value;
      const allowed = [...dialog.querySelectorAll('[data-nv-country-option]')].some(option => option.dataset.countryCode === value);
      if (!allowed || value === dialog.dataset.currentCountry) event.preventDefault();
    });
    on(menu, 'toggle', () => { if (menu.open) reveal(); direction = 0; travel = 0; });
    on(menu, 'click', (event) => { if (event.target.closest('a')) menu.open = false; });
    on(header, 'focusin', reveal);
    on(document, 'keydown', (event) => {
      if (event.key === 'Escape' && !dialog.open && menu?.open) {
        menu.open = false; menu.querySelector('summary')?.focus({ preventScroll: true });
      }
    });
    on(document, 'click', (event) => {
      if (menu?.open && !menu.contains(event.target)) menu.open = false;
    });
    on(window, 'scroll', schedule, { passive: true });
    on(wrapper, 'scroll', schedule, { passive: true });
    on(window, 'resize', scheduleMeasure);
    on(window, 'pageshow', scheduleMeasure);
    // Switching from wrapper width to viewport width is not a new scrolling layout.
    const observer = 'ResizeObserver' in window ? new ResizeObserver(() => {
      if (header.offsetHeight !== measuredHeaderHeight || (announcement?.offsetHeight || 0) !== measuredAnnouncementHeight) scheduleMeasure();
    }) : null;
    observer?.observe(header);
    if (announcement) observer?.observe(announcement);
    measure();
    host.dataset.nvHeaderReady = 'true';
    host.nvCleanup = () => {
      events.abort(); cancelAnimationFrame(frame); cancelAnimationFrame(resizeFrame); observer?.disconnect();
      if (dialog.open) dialog.close();
      restoreScrollLock();
      header.classList.remove('is-fixed', 'is-scroll-hidden');
      space.style.height = '';
      delete host.dataset.nvHeaderReady;
    };
  });
};

/* Explicit TouchEvents for Safari's transformed sliders. Native scroll rails do not use this. */
const bindNuvoriteTouchSwipe = (target, on, start, move, end) => {
  let activeTouch = null, blocked = false;
  const input = (event, touch) => ({
    type: event.type, pointerId: touch.identifier, pointerType: 'touch', isPrimary: true,
    clientX: touch.clientX, clientY: touch.clientY, touchInput: true,
    preventDefault: () => { if (event.cancelable !== false) event.preventDefault(); }
  });
  const cancel = (event) => {
    const previous = activeTouch;
    activeTouch = null;
    if (previous) end(input(event, previous), true);
  };
  on(target, 'touchstart', (event) => {
    if (event.touches.length !== 1) { blocked = true; cancel(event); return; }
    if (blocked || activeTouch) return;
    activeTouch = event.touches[0];
    start(input(event, activeTouch));
  }, { passive: true });
  on(target, 'touchmove', (event) => {
    if (event.touches.length !== 1) { blocked = true; cancel(event); return; }
    if (blocked || !activeTouch) return;
    const touch = Array.from(event.touches).find(item => item.identifier === activeTouch.identifier);
    if (!touch) return;
    activeTouch = touch;
    move(input(event, touch));
  }, { passive: false });
  on(target, 'touchend', (event) => {
    const touch = activeTouch && Array.from(event.changedTouches).find(item => item.identifier === activeTouch.identifier);
    if (touch) { activeTouch = null; end(input(event, touch)); }
    if (!event.touches.length) blocked = false;
  }, { passive: true });
  on(target, 'touchcancel', (event) => {
    cancel(event);
    if (!event.touches.length) blocked = false;
  }, { passive: true });
  const reset = () => { activeTouch = null; blocked = false; };
  on(window, 'blur', reset);
  on(document, 'visibilitychange', reset);
};

/* Readable announcement pages: hold, then travel left, including the last -> first wrap. */
const initNuvoriteAnnouncements = (root = document) => {
  root.querySelectorAll('[data-nv-announcement-rotator]:not([data-nv-announcement-ready])').forEach((host) => {
    const viewport = host.querySelector('[data-nv-announcement-viewport]');
    const stage = host.querySelector('[data-nv-announcement-stage]');
    const toggle = host.querySelector('[data-nv-announcement-toggle]');
    const messages = [...host.querySelectorAll('[data-nv-announcement-message]')];
    if (!viewport || !stage || !toggle || !messages.length) return;
    const events = new AbortController();
    const on = (target, name, callback, options = {}) => target?.addEventListener(name, callback, { ...options, signal: events.signal });
    let pages = [], groups = [], active = 0, animations = [];
    let timer, transitionTimer, resizeFrame, measuredWidth = 0, destroyed = false, gesture = null;
    // Keep the explicitly requested rotation enabled, with a visible, keyboard-accessible pause.
    let paused = false;
    const stop = () => window.clearTimeout(timer);
    const cancelTransition = () => {
      window.clearTimeout(transitionTimer);
      animations.forEach(animation => animation.cancel());
      animations = [];
    };
    const paint = () => {
      pages.forEach((page, index) => {
        page.hidden = index !== active;
        page.style.transform = 'translate3d(0,0,0)';
      });
      host.dataset.nvAnnouncementPage = String(active + 1);
    };
    const play = () => {
      stop();
      if (!destroyed && !paused && !gesture && !document.hidden && pages.length > 1) timer = window.setTimeout(next, 4000);
    };
    const finish = () => { cancelTransition(); paint(); play(); };
    const wrap = value => ((value % pages.length) + pages.length) % pages.length;
    const goTo = (target, from = active, duration = 700) => {
      stop(); cancelTransition();
      if (destroyed || pages.length < 2) return;
      active = wrap(target);
      host.dataset.nvAnnouncementPage = String(active + 1);
      if (Math.abs(target - from) < .001 || typeof pages[active].animate !== 'function') { finish(); return; }
      const options = { duration, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' };
      pages.forEach((page, index) => {
        let end = wrap(index - target);
        if (end > pages.length / 2 || (end === pages.length / 2 && target >= from)) end -= pages.length;
        const start = end + target - from;
        page.hidden = Math.min(start, end) >= 1 || Math.max(start, end) <= -1;
        if (!page.hidden) animations.push(page.animate([
          { transform: `translate3d(${start * 100}%,0,0)` },
          { transform: `translate3d(${end * 100}%,0,0)` }
        ], options));
      });
      transitionTimer = window.setTimeout(finish, options.duration);
    };
    const next = () => {
      if (paused || gesture || document.hidden || destroyed) return;
      host.dataset.nvAnnouncementInput = 'autoplay';
      goTo(active + 1);
    };
    const releaseGesture = () => {
      const previous = gesture;
      gesture = null;
      host.classList.remove('is-dragging');
      try { if (previous && !previous.touchInput && viewport.hasPointerCapture?.(previous.id)) viewport.releasePointerCapture(previous.id); } catch (_) { /* Capture can end before touch/pointer cancellation. */ }
      return previous;
    };
    const paintDrag = position => {
      pages.forEach((page, index) => {
        const offset = wrap(index - position + pages.length / 2) - pages.length / 2;
        page.hidden = Math.abs(offset) >= 1;
        page.style.transform = `translate3d(${offset * 100}%,0,0)`;
      });
    };
    const updateToggle = () => {
      const label = paused ? 'Play announcements' : 'Pause announcements';
      toggle.setAttribute('aria-label', label);
      toggle.title = label;
      toggle.dataset.paused = String(paused);
      host.dataset.nvAnnouncementPaused = String(paused);
    };
    const layout = () => {
      resizeFrame = null;
      if (destroyed || !viewport.clientWidth) return;
      stop(); cancelTransition();
      releaseGesture();
      const currentMessage = groups[active]?.[0] || 0;
      measuredWidth = viewport.clientWidth;
      const gap = parseFloat(getComputedStyle(host).getPropertyValue('--nv-announcement-gap')) || 26;
      const widths = messages.map(message => message.getBoundingClientRect().width);
      groups = [];
      for (let index = 0; index < messages.length; index++) {
        const group = [index];
        // Use real font metrics, not character count; never shrink text to squeeze in a pair.
        if (index + 1 < messages.length && widths[index] + widths[index + 1] + gap <= measuredWidth - 8) group.push(++index);
        groups.push(group);
      }
      pages = groups.map(group => {
        const page = document.createElement('div');
        page.className = 'nv-announcement-slide';
        group.forEach(index => {
          const message = document.createElement('span');
          message.className = 'nv-announcement-message';
          message.textContent = messages[index].textContent;
          page.append(message);
        });
        return page;
      });
      stage.replaceChildren(...pages);
      stage.hidden = false;
      // Reserve the tallest page once, including future longer copy and enlarged browser text.
      host.style.setProperty('--nv-announcement-height', `${Math.max(40, ...pages.map(page => Math.ceil(page.getBoundingClientRect().height)))}px`);
      active = Math.max(0, groups.findIndex(group => group.includes(currentMessage)));
      host.dataset.nvAnnouncementPages = String(pages.length);
      toggle.hidden = pages.length < 2;
      paint(); updateToggle(); play();
    };
    const scheduleLayout = () => { cancelAnimationFrame(resizeFrame); resizeFrame = requestAnimationFrame(layout); };
    on(toggle, 'click', () => { paused = !paused; updateToggle(); if (paused) stop(); else play(); });
    const startGesture = (event) => {
      if (pages.length < 2 || gesture || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
      stop();
      // Freeze the actual in-flight position, so grabbing mid-transition never jumps to an endpoint.
      const offset = (pages[active].getBoundingClientRect().left - viewport.getBoundingClientRect().left) / viewport.clientWidth;
      const position = active - (Number.isFinite(offset) ? offset : 0);
      cancelTransition();
      gesture = { id: event.pointerId, touchInput: event.touchInput, x: event.clientX, y: event.clientY, time: performance.now(), origin: position, position, axis: null, delta: 0 };
      host.dataset.nvAnnouncementInput = `pointer-${event.pointerType || 'unknown'}`;
      paintDrag(position);
    };
    const moveGesture = (event) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (!gesture.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { const previous = releaseGesture(); goTo(Math.round(previous.position), previous.position, 500); return; }
        gesture.axis = 'x';
        try { if (!event.touchInput) viewport.setPointerCapture?.(event.pointerId); } catch (_) { /* Window listeners also cover uncaptured mouse movement. */ }
        host.classList.add('is-dragging');
      }
      event.preventDefault();
      gesture.delta = Math.max(-viewport.clientWidth, Math.min(viewport.clientWidth, dx));
      gesture.position = gesture.origin - gesture.delta / viewport.clientWidth;
      paintDrag(gesture.position);
    };
    const endGesture = (event, cancelled = false) => {
      if (!gesture || (event.pointerId != null && event.pointerId !== gesture.id)) return;
      const previous = releaseGesture();
      let target = Math.round(previous.position);
      const distance = Math.abs(previous.delta);
      const intentional = previous.axis === 'x' && (distance > viewport.clientWidth * .16 || (distance > 14 && performance.now() - previous.time < 450));
      if (!cancelled && intentional) target = previous.delta < 0 ? Math.max(target, Math.floor(previous.origin) + 1) : Math.min(target, Math.ceil(previous.origin) - 1);
      host.dataset.nvAnnouncementInput = previous.axis === 'x' ? (previous.delta < 0 ? 'drag-left' : 'drag-right') : 'tap-or-scroll';
      goTo(target, previous.position, 500);
    };
    // A Safari finger emits both event families; exactly one family may own a gesture.
    const pointerOnly = callback => event => {
      if (event.pointerType === 'touch' && 'ontouchstart' in window) return;
      callback(event);
    };
    on(viewport, 'pointerdown', pointerOnly(startGesture), { passive: true });
    on(window, 'pointermove', pointerOnly(moveGesture), { passive: false });
    on(window, 'pointerup', pointerOnly(endGesture));
    on(window, 'pointercancel', pointerOnly(event => endGesture(event, true)));
    on(viewport, 'lostpointercapture', pointerOnly(event => { if (!gesture?.touchInput) endGesture(event, true); }));
    bindNuvoriteTouchSwipe(viewport, on, startGesture, moveGesture, endGesture);
    on(viewport, 'dragstart', event => event.preventDefault());
    on(window, 'blur', event => endGesture(event, true));
    on(toggle, 'keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const offset = (pages[active].getBoundingClientRect().left - viewport.getBoundingClientRect().left) / viewport.clientWidth;
      const position = active - (Number.isFinite(offset) ? offset : 0);
      releaseGesture();
      host.dataset.nvAnnouncementInput = event.key === 'ArrowRight' ? 'key-next' : 'key-previous';
      goTo(Math.round(position) + (event.key === 'ArrowRight' ? 1 : -1), position, 500);
    });
    on(document, 'visibilitychange', () => { releaseGesture(); cancelTransition(); paint(); play(); });
    on(window, 'resize', scheduleLayout);
    on(window, 'pageshow', scheduleLayout);
    on(document.fonts, 'loadingdone', scheduleLayout);
    const observer = 'ResizeObserver' in window ? new ResizeObserver(() => {
      if (viewport.clientWidth !== measuredWidth) scheduleLayout();
    }) : null;
    observer?.observe(viewport);
    host.dataset.nvAnnouncementReady = 'true';
    layout();
    document.fonts?.ready.then(() => { if (!destroyed) scheduleLayout(); });
    host.nvCleanup = () => {
      destroyed = true; stop(); cancelTransition(); releaseGesture(); events.abort(); observer?.disconnect(); cancelAnimationFrame(resizeFrame);
      stage.replaceChildren(); stage.hidden = true; toggle.hidden = true;
      host.style.removeProperty('--nv-announcement-height');
      delete host.dataset.nvAnnouncementReady;
      delete host.dataset.nvAnnouncementPage;
      delete host.dataset.nvAnnouncementPages;
      delete host.dataset.nvAnnouncementPaused;
      delete host.dataset.nvAnnouncementInput;
    };
  });
};

const initNuvoriteHeroCarousels = (root = document) => {
  root.querySelectorAll('[data-nv-hero-carousel]:not([data-nv-hero-ready])').forEach((carousel) => {
    const track = carousel.querySelector('.nv-hero-carousel__track');
    const slides = [...carousel.querySelectorAll('[data-nv-hero-slide]')];
    if (!track || slides.length < 2) return;

    const autoplayDelay = Number(carousel.dataset.autoplay) || 4000;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const events = new AbortController();
    const on = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: events.signal });
    let activeIndex = -1;
    let position = 0;
    let autoplayTimer;
    let animationFrame;
    let gesture = null;
    let suppressClickUntil = 0;
    let layoutWidth = track.clientWidth;

    const wrap = (value) => ((value % slides.length) + slides.length) % slides.length;
    const paint = (value) => {
      position = value;
      // Recycle only offscreen slides: 4 -> 1 travels one panel, with no cloned links/countdown.
      slides.forEach((slide, index) => {
        const offset = wrap(index - position + slides.length / 2) - slides.length / 2;
        slide.style.transform = `translate3d(${offset * 100}%,0,0)`;
      });
      carousel.dataset.nvPosition = position.toFixed(4);
      const nextIndex = wrap(Math.round(position));
      if (nextIndex === activeIndex) return;
      activeIndex = nextIndex;
      carousel.dataset.nvActiveSlide = String(activeIndex + 1);
      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== activeIndex));
        const link = slide.querySelector('a');
        if (link) link.tabIndex = index === activeIndex ? 0 : -1;
      });
    };
    const cancelAnimation = () => {
      cancelAnimationFrame(animationFrame);
      carousel.classList.remove('is-animating');
    };
    const stopAutoplay = () => window.clearTimeout(autoplayTimer);
    const startAutoplay = () => {
      stopAutoplay();
      if (!document.hidden && !gesture) {
        autoplayTimer = window.setTimeout(() => goTo(Math.round(position) + 1), autoplayDelay);
      }
    };
    const goTo = (target, { instant = false, released = false } = {}) => {
      cancelAnimation();
      const origin = position;
      const distance = target - origin;
      const reduceMotion = motion.matches && carousel.dataset.motionPreference !== 'smooth';
      if (instant || reduceMotion || Math.abs(distance) < .001) {
        paint(wrap(target));
      } else {
        const duration = released ? 540 : 660;
        const started = performance.now();
        carousel.classList.add('is-animating');
        const animate = (now) => {
          const progress = Math.min((now - started) / duration, 1);
          // Gentle ease-out after a swipe; symmetrical acceleration for automatic slides.
          const eased = released ? 1 - Math.pow(1 - progress, 3) :
            (progress < .5 ? 4 * progress ** 3 : 1 - Math.pow(-2 * progress + 2, 3) / 2);
          paint(origin + distance * eased);
          if (progress < 1) animationFrame = requestAnimationFrame(animate);
          else {
            paint(wrap(target));
            carousel.classList.remove('is-animating');
          }
        };
        animationFrame = requestAnimationFrame(animate);
      }
      startAutoplay();
    };

    const startGesture = (event) => {
      if (gesture || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
      cancelAnimation();
      gesture = {
        id: event.pointerId, touchInput: event.touchInput, x: event.clientX, y: event.clientY,
        position, dragging: false, lastX: event.clientX,
        lastTime: performance.now(), velocity: 0
      };
      stopAutoplay();
    };

    const moveGesture = (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.dragging && Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
        gesture = null;
        goTo(Math.round(position), { released: true });
        return;
      }
      if (!gesture.dragging && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
        gesture.dragging = true;
        track.classList.add('is-dragging');
        try { if (!event.touchInput) track.setPointerCapture?.(event.pointerId); } catch (_) { /* Pointer capture is an optional mouse enhancement. */ }
      }
      if (gesture.dragging) {
        event.preventDefault();
        const now = performance.now();
        gesture.velocity = (event.clientX - gesture.lastX) / Math.max(now - gesture.lastTime, 1);
        gesture.lastX = event.clientX;
        gesture.lastTime = now;
        paint(gesture.position - dx / Math.max(track.clientWidth, 1));
      }
    };

    const endGesture = (event, cancelled = false) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const completed = gesture;
      gesture = null;
      track.classList.remove('is-dragging');
      try { if (!completed.touchInput && track.hasPointerCapture?.(completed.id)) track.releasePointerCapture(completed.id); } catch (_) { /* Capture may already have been released by Safari. */ }
      if (completed.dragging) {
        suppressClickUntil = Date.now() + 500;
        const movement = position - completed.position;
        const velocity = performance.now() - completed.lastTime < 120 ? completed.velocity : 0;
        let target = Math.round(position);
        const origin = Math.round(completed.position);
        if (!cancelled && event.type !== 'pointercancel' && target === origin &&
          (Math.abs(movement) > .14 || Math.abs(velocity) > .4)) {
          target = origin + (Math.abs(velocity) > .4 ? (velocity < 0 ? 1 : -1) : Math.sign(movement));
        }
        goTo(target, { released: true });
      } else goTo(Math.round(position), { released: true });
    };
    const pointerOnly = callback => event => {
      if (event.pointerType === 'touch' && 'ontouchstart' in window) return;
      callback(event);
    };
    on(track, 'pointerdown', pointerOnly(startGesture), { passive: true });
    on(window, 'pointermove', pointerOnly(moveGesture), { passive: false });
    on(window, 'pointerup', pointerOnly(endGesture), { passive: true });
    on(window, 'pointercancel', pointerOnly(event => endGesture(event, true)), { passive: true });
    on(track, 'lostpointercapture', pointerOnly(event => { if (!gesture?.touchInput) endGesture(event, true); }));
    bindNuvoriteTouchSwipe(track, on, startGesture, moveGesture, endGesture);
    on(track, 'dragstart', (event) => event.preventDefault());
    on(track, 'click', (event) => {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, { capture: true });
    on(track, 'keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo(Math.round(position) + (event.key === 'ArrowRight' ? 1 : -1));
    });
    const clearGesture = () => {
      const previous = gesture;
      gesture = null;
      try { if (previous && !previous.touchInput && track.hasPointerCapture?.(previous.id)) track.releasePointerCapture(previous.id); } catch (_) { /* Already released. */ }
      track.classList.remove('is-dragging');
    };
    on(window, 'blur', () => {
      clearGesture();
      goTo(Math.round(position), { released: true });
    });
    on(document, 'visibilitychange', () => {
      clearGesture();
      goTo(Math.round(position), { instant: true });
    });
    const resize = () => {
      if (layoutWidth === track.clientWidth) return;
      layoutWidth = track.clientWidth;
      clearGesture();
      goTo(Math.round(position), { instant: true });
    };
    const observer = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (observer) observer.observe(track);
    else on(window, 'resize', resize);

    carousel.nvCleanup = () => {
      stopAutoplay();
      cancelAnimation();
      clearGesture();
      events.abort();
      observer?.disconnect();
      slides.forEach((slide) => {
        slide.style.transform = '';
        slide.removeAttribute('aria-hidden');
        const link = slide.querySelector('a');
        if (link) link.tabIndex = 0;
      });
      delete carousel.dataset.nvHeroReady;
      delete carousel.dataset.nvPosition;
    };
    track.scrollLeft = 0;
    paint(0);
    carousel.dataset.nvHeroReady = 'true';
    startAutoplay();
  });
};

const initNuvoriteCountdowns = (root = document) => {
  root.querySelectorAll('[data-nv-countdown]:not([data-nv-countdown-ready])').forEach((countdown) => {
    const cycleHours = Math.max(Number(countdown.dataset.cycleHours) || 96, 1);
    const cycleMilliseconds = cycleHours * 60 * 60 * 1000;
    const storageKey = countdown.dataset.storageKey || 'nv-autumn-sale-cycle';
    const fields = {
      days: countdown.querySelector('[data-nv-days]'),
      hours: countdown.querySelector('[data-nv-hours]'),
      minutes: countdown.querySelector('[data-nv-minutes]'),
      seconds: countdown.querySelector('[data-nv-seconds]'),
    };
    if (Object.values(fields).some((field) => !field)) return;

    countdown.dataset.nvCountdownReady = 'true';
    let endTime;
    try { endTime = Number(window.localStorage.getItem(storageKey)); } catch (error) { endTime = 0; }
    if (!Number.isFinite(endTime) || endTime <= 0 || endTime > Date.now() + cycleMilliseconds) {
      endTime = Date.now() + cycleMilliseconds;
    }

    const saveEndTime = () => {
      try { window.localStorage.setItem(storageKey, String(endTime)); } catch (error) { /* Storage can be disabled. */ }
    };
    const pad = (value) => String(value).padStart(2, '0');
    const update = () => {
      const now = Date.now();
      if (now >= endTime) {
        const completedCycles = Math.floor((now - endTime) / cycleMilliseconds) + 1;
        endTime += completedCycles * cycleMilliseconds;
        saveEndTime();
      }

      const remaining = Math.max(endTime - now, 0);
      const totalSeconds = Math.ceil(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      fields.days.textContent = pad(days);
      fields.hours.textContent = pad(hours);
      fields.minutes.textContent = pad(minutes);
      fields.seconds.textContent = pad(seconds);
      countdown.setAttribute('aria-label', `Offer countdown: ${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds remaining`);
    };

    saveEndTime();
    update();
    countdown.nvTimer = window.setInterval(update, 1000);
  });
};

const initNuvoriteProductSliders = (root = document) => {
  root.querySelectorAll('[data-nv-product-slider]:not([data-nv-slider-ready]), [data-nv-manual-slider]:not([data-nv-slider-ready])').forEach((slider) => {
    const track = slider.querySelector('.nv-product-grid') || slider.querySelector('[data-nv-manual-track]');
    const thumb = slider.querySelector('.nv-product-scrollbar span') || slider.querySelector('[data-nv-manual-progress]');
    const cards = [...slider.querySelectorAll('.nv-product-card, [data-nv-manual-card]')];
    const previous = slider.querySelector('[data-nv-manual-prev]');
    const next = slider.querySelector('[data-nv-manual-next]');
    if (!track || !thumb || !cards.length) return;
    const events = new AbortController();
    const on = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: events.signal });
    const maxScroll = () => Math.max(track.scrollWidth - track.clientWidth, 0);
    const clamp = (value) => Math.max(0, Math.min(value, maxScroll()));
    const step = () => cards.length > 1 ? cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left : track.clientWidth;
    const nearestStop = (value) => {
      const first = cards[0].getBoundingClientRect().left;
      const stops = cards.map((card) => clamp(card.getBoundingClientRect().left - first));
      return stops.reduce((closest, stop) => Math.abs(stop - value) < Math.abs(closest - value) ? stop : closest, 0);
    };
    let progressFrame, animationFrame, gesture = null, suppressClickUntil = 0;
    let layoutWidth = track.clientWidth;
    const update = () => {
      cancelAnimationFrame(progressFrame);
      progressFrame = requestAnimationFrame(() => {
        const bar = thumb.parentElement;
        const contentWidth = Math.max(track.scrollWidth, 1);
        const maximum = maxScroll();
        const barWidth = bar.clientWidth;
        const thumbWidth = Math.max(barWidth * (track.clientWidth / contentWidth), 42);
        const travel = Math.max(barWidth - thumbWidth, 0);
        const progress = maximum ? clamp(track.scrollLeft) / maximum : 0;
        slider.dataset.nvSliderScrollable = String(maximum > 1);
        if (previous) previous.disabled = maximum <= 1 || track.scrollLeft <= 1;
        if (next) next.disabled = maximum <= 1 || track.scrollLeft >= maximum - 1;
        thumb.style.width = `${Math.min(thumbWidth, barWidth)}px`;
        thumb.style.transform = `translate3d(${travel * progress}px, 0, 0)`;
      });
    };
    const cancelAnimation = () => cancelAnimationFrame(animationFrame);
    const paint = (value) => { track.scrollLeft = clamp(value); update(); };
    const settle = (value, instant = false) => {
      cancelAnimation();
      const target = clamp(value), origin = track.scrollLeft, started = performance.now();
      const finish = () => {
        paint(target);
        track.classList.remove('is-interacting');
        track.classList.remove('is-dragging');
      };
      if (instant || Math.abs(target - origin) < .5) { finish(); return; }
      track.classList.add('is-interacting');
      const animate = (now) => {
        const progress = Math.min((now - started) / 420, 1);
        paint(origin + (target - origin) * (1 - (1 - progress) ** 3));
        if (progress < 1) animationFrame = requestAnimationFrame(animate);
        else finish();
      };
      animationFrame = requestAnimationFrame(animate);
    };
    const clearGesture = () => {
      const previous = gesture;
      gesture = null;
      try { if (previous && track.hasPointerCapture?.(previous.id)) track.releasePointerCapture(previous.id); } catch (_) { /* Already released. */ }
      track.classList.remove('is-dragging');
    };
    // Fingers use the browser's native overflow, momentum and snap, including on iPhone Safari.
    // Never write scrollLeft on touchmove/end/cancel: pointercancel often starts native momentum.
    const nativeScrollStart = () => {
      cancelAnimation(); clearGesture();
      track.classList.remove('is-interacting');
      suppressClickUntil = 0;
    };
    on(track, 'touchstart', nativeScrollStart, { passive: true });
    on(track, 'pointerdown', (event) => {
      if (event.pointerType === 'touch') { nativeScrollStart(); return; }
      if (maxScroll() <= 1 || gesture || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
      cancelAnimation();
      // Only mouse/pen dragging needs scripted scrolling; touch keeps native snapping.
      track.classList.add('is-interacting');
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: track.scrollLeft,
        dragging: false, lastX: event.clientX, lastTime: performance.now(), velocity: 0 };
    }, { passive: true });
    on(window, 'pointermove', (event) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (!gesture.dragging && Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
        clearGesture();
        track.classList.remove('is-interacting');
        return; // Let the browser own vertical page scrolling and pinch zoom.
      }
      if (!gesture.dragging && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
        gesture.dragging = true;
        try { track.setPointerCapture?.(event.pointerId); } catch (_) { /* Window listeners handle dragging without capture. */ }
        track.classList.add('is-dragging');
      }
      if (!gesture.dragging) return;
      event.preventDefault();
      const now = performance.now();
      gesture.velocity = (event.clientX - gesture.lastX) / Math.max(now - gesture.lastTime, 1);
      gesture.lastX = event.clientX;
      gesture.lastTime = now;
      paint(gesture.left - dx);
    }, { passive: false });
    const release = (event) => {
      if (!gesture || (event.pointerId != null && event.pointerId !== gesture.id)) return;
      const completed = gesture;
      clearGesture();
      if (!completed.dragging) { settle(nearestStop(track.scrollLeft)); return; }
      suppressClickUntil = Date.now() + 450;
      const movement = track.scrollLeft - completed.left;
      const velocity = performance.now() - completed.lastTime < 120 ? completed.velocity : 0;
      let target = nearestStop(track.scrollLeft);
      const origin = nearestStop(completed.left);
      if (event.type !== 'pointercancel' && target === origin &&
          (Math.abs(movement) > step() * .14 || Math.abs(velocity) > .4)) {
        target = origin + (Math.abs(velocity) > .4 ? -Math.sign(velocity) : Math.sign(movement)) * step();
      }
      settle(target);
    };
    on(window, 'pointerup', release, { passive: true });
    on(window, 'pointercancel', release, { passive: true });
    on(track, 'lostpointercapture', event => release({ pointerId: event.pointerId, type: 'pointercancel' }), { passive: true });
    on(window, 'blur', release);
    on(track, 'dragstart', (event) => event.preventDefault());
    on(track, 'click', (event) => {
      if (Date.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
    }, { capture: true });
    on(track, 'keydown', (event) => {
      if (maxScroll() <= 1 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      clearGesture();
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? maxScroll() :
        nearestStop(track.scrollLeft) + (event.key === 'ArrowRight' ? 1 : -1) * step();
      settle(target);
    });
    const moveByCard = (direction) => {
      clearGesture();
      settle(nearestStop(track.scrollLeft) + direction * step());
    };
    if (previous) on(previous, 'click', () => moveByCard(-1));
    if (next) on(next, 'click', () => moveByCard(1));
    on(track, 'wheel', () => {
      cancelAnimation(); clearGesture(); track.classList.remove('is-interacting');
    }, { passive: true });
    on(track, 'scroll', update, { passive: true });
    const resize = () => {
      if (layoutWidth !== track.clientWidth) {
        layoutWidth = track.clientWidth;
        clearGesture();
        settle(nearestStop(track.scrollLeft), true);
      }
      update();
    };
    const observer = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (observer) observer.observe(track);
    else on(window, 'resize', resize);
    slider.nvCleanup = () => {
      events.abort(); cancelAnimation(); cancelAnimationFrame(progressFrame);
      observer?.disconnect(); clearGesture();
      track.classList.remove('is-interacting');
      delete slider.dataset.nvSliderReady;
      delete slider.dataset.nvSliderScrollable;
    };
    slider.dataset.nvSliderReady = 'true';
    slider.dataset.nvSliderScrollable = String(maxScroll() > 1);
    update();
  });
};

/* Native touch momentum + slow continuous scrolling, with accessible manual controls. */
const initNuvoriteResults = (root = document) => {
  root.querySelectorAll('[data-nv-results]:not([data-nv-results-ready])').forEach((rail) => {
    const track = rail.querySelector('.nv-results-track');
    const cards = [...rail.querySelectorAll('[data-nv-result-card]')];
    if (!track || cards.length < 2) return;
    const events = new AbortController();
    const on = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: events.signal });
    const toggle = rail.querySelector('[data-nv-results-toggle]');
    const symbol = rail.querySelector('[data-nv-results-symbol]');
    const thumb = rail.querySelector('.nv-results-progress>span');
    const speed = Math.max(4, Math.min(Number(rail.dataset.speed) || 12, 30));
    let paused = rail.dataset.autoplay !== 'true';
    let step = 0, cycle = 0, position = 0, expected = 0;
    let frame, settleTimer, lastTime = performance.now(), resumeAt = lastTime + 1800;
    let inView = true, gesture = null, suppressClickUntil = 0;
    const clones = [];
    const cloneCard = (card) => {
      const clone = card.cloneNode(true);
      clone.removeAttribute('data-nv-result-card');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clones.push(clone);
      return clone;
    };
    track.prepend(...cards.map(cloneCard));
    track.append(...cards.map(cloneCard));
    const wrap = (value) => cycle ? ((value - cycle) % cycle + cycle) % cycle + cycle : value;
    const progress = () => {
      if (thumb && cycle) thumb.style.transform = `translateX(${((track.scrollLeft - cycle) % cycle + cycle) % cycle / cycle * 200}%)`;
    };
    const normalize = () => {
      if (!cycle) return;
      const corrected = wrap(track.scrollLeft);
      const shift = corrected - track.scrollLeft;
      if (Math.abs(shift) > 1) {
        track.scrollLeft = corrected;
        if (gesture) gesture.left += shift;
      }
      position = track.scrollLeft;
      expected = position;
      progress();
    };
    const layout = () => {
      const oldCycle = cycle;
      const phase = oldCycle ? ((position - oldCycle) % oldCycle + oldCycle) % oldCycle / oldCycle : 0;
      step = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
      cycle = step * cards.length;
      position = cycle * (1 + phase);
      expected = position;
      track.scrollLeft = position;
      progress();
    };
    const pauseAfterInteraction = () => { resumeAt = performance.now() + 3500; };
    // Let native smooth scrolling / touch momentum finish in the cloned group.
    // Repositioning during an active native animation cancels the remaining glide.
    const settleAfterScroll = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => { if (!gesture) normalize(); }, 160);
    };
    const updateToggle = () => {
      toggle?.setAttribute('aria-pressed', String(paused));
      toggle?.setAttribute('aria-label', paused ? 'Start automatic scrolling' : 'Pause automatic scrolling');
      if (symbol) symbol.textContent = paused ? '▷' : 'Ⅱ';
    };
    on(track, 'scroll', () => {
      if (Math.abs(track.scrollLeft - expected) > 1.5) {
        pauseAfterInteraction();
        position = track.scrollLeft;
        expected = position;
        progress();
        settleAfterScroll();
      } else progress();
    }, { passive: true });
    on(track, 'wheel', pauseAfterInteraction, { passive: true });
    on(track, 'pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      gesture = { id: event.pointerId, type: event.pointerType, x: event.clientX, left: track.scrollLeft, dragging: false };
      pauseAfterInteraction();
    }, { passive: true });
    on(window, 'pointermove', (event) => {
      if (!gesture || gesture.id !== event.pointerId || gesture.type !== 'mouse') return;
      const delta = event.clientX - gesture.x;
      if (!gesture.dragging && Math.abs(delta) > 5) {
        gesture.dragging = true;
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
      }
      if (!gesture.dragging) return;
      event.preventDefault();
      track.scrollLeft = gesture.left - delta;
      normalize();
      pauseAfterInteraction();
    }, { passive: false });
    const end = (event) => {
      if (!gesture || (event.pointerId != null && event.pointerId !== gesture.id)) return;
      if (gesture.dragging) suppressClickUntil = Date.now() + 400;
      if (track.hasPointerCapture(gesture.id)) track.releasePointerCapture(gesture.id);
      gesture = null;
      track.classList.remove('is-dragging');
      pauseAfterInteraction();
      settleAfterScroll();
    };
    on(window, 'pointerup', end, { passive: true });
    on(window, 'pointercancel', end, { passive: true });
    on(window, 'blur', end);
    on(track, 'dragstart', (event) => event.preventDefault());
    on(track, 'click', (event) => {
      if (Date.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
    }, { capture: true });
    const advance = (direction) => {
      pauseAfterInteraction();
      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    };
    on(rail.querySelector('[data-nv-results-prev]'), 'click', () => advance(-1));
    on(rail.querySelector('[data-nv-results-next]'), 'click', () => advance(1));
    on(toggle, 'click', () => { paused = !paused; resumeAt = performance.now(); updateToggle(); });
    on(track, 'keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      advance(event.key === 'ArrowRight' ? 1 : -1);
    });
    on(document, 'visibilitychange', () => { end({}); lastTime = performance.now(); });
    const resize = 'ResizeObserver' in window ? new ResizeObserver(layout) : null;
    resize?.observe(track);
    if (!resize) on(window, 'resize', layout);
    const visibility = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => { inView = entries[0].isIntersecting; }, { rootMargin: '80px' }) : null;
    visibility?.observe(rail);
    const tick = (time) => {
      const elapsed = Math.min(Math.max(time - lastTime, 0), 48);
      lastTime = time;
      if (!paused && !gesture && !document.hidden && inView && time >= resumeAt && cycle) {
        position = wrap(position + speed * elapsed / 1000);
        track.scrollLeft = position;
        expected = track.scrollLeft;
        progress();
      }
      frame = requestAnimationFrame(tick);
    };
    rail.nvCleanup = () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      events.abort();
      resize?.disconnect();
      visibility?.disconnect();
      clones.forEach((clone) => clone.remove());
      track.scrollLeft = 0;
      delete rail.dataset.nvResultsReady;
    };
    layout();
    updateToggle();
    rail.dataset.nvResultsReady = 'true';
    frame = requestAnimationFrame(tick);
  });
};

initNuvoriteHeroCarousels();
initNuvoriteHeaders();
initNuvoriteAnnouncements();
initNuvoriteCountdowns();
initNuvoriteProductSliders();
initNuvoriteResults();
document.addEventListener('shopify:section:load', (event) => {
  initNuvoriteHeaders(event.target);
  initNuvoriteAnnouncements(event.target);
  initNuvoriteHeroCarousels(event.target);
  initNuvoriteCountdowns(event.target);
  initNuvoriteProductSliders(event.target);
  initNuvoriteResults(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-nv-announcement-rotator]').forEach((announcement) => announcement.nvCleanup?.());
  event.target.querySelectorAll('[data-nv-header-root]').forEach((header) => header.nvCleanup?.());
  event.target.querySelectorAll('[data-nv-hero-carousel]').forEach((carousel) => carousel.nvCleanup?.());
  event.target.querySelectorAll('[data-nv-product-slider], [data-nv-manual-slider]').forEach((slider) => slider.nvCleanup?.());
  event.target.querySelectorAll('[data-nv-countdown]').forEach((countdown) => window.clearInterval(countdown.nvTimer));
  event.target.querySelectorAll('[data-nv-results]').forEach((rail) => rail.nvCleanup?.());
});

/* Submit market changes only after a customer uses one of the native footer selects. */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-nv-localization-select]');
  if (select?.form) select.form.submit();
});
